export const SEARCHING_DATASETS_MESSAGE = 'This might take a long time';

export const RETRIEVAL_AGENT_TERMINAL_STATUSES = new Set(['completed', 'failed', 'agent_failure', 'cancelled']);

const VISIBLE_STEPS = [
    'queued',
    'initializing',
    'task_understanding',
    'data_searching',
    'data_preparing',
    'data_exporting',
    'uploading_dataset',
];

const RETRIEVAL_STEPS_BEFORE_UPLOAD = VISIBLE_STEPS.filter((id) => id !== 'uploading_dataset');

const STEP_LABELS = {
    queued: 'Queued',
    initializing: 'Initializing',
    task_understanding: 'Understanding the request',
    data_searching: 'Searching public datasets',
    data_preparing: 'Preparing data',
    data_exporting: 'Exporting',
    uploading_dataset: 'Uploading dataset',
};

/**
 * Subtitle for an active "Queued" step: how many jobs run before this one.
 * Shared by the Retrieval Agent and Generate-with-AI progress cards.
 */
export function queuedWaitingSubtitle(jobsAhead) {
    const ahead = Number(jobsAhead);
    if (!Number.isFinite(ahead) || ahead <= 0) return 'Starting soon';
    return ahead === 1 ? '1 job ahead' : `${ahead} jobs ahead`;
}

function normalizePhase(status) {
    if (!status) return 'initializing';
    if (status === 'completed') return 'completed';
    if (RETRIEVAL_AGENT_TERMINAL_STATUSES.has(status) && status !== 'completed') {
        return status;
    }
    return VISIBLE_STEPS.includes(status) ? status : 'initializing';
}

export function retrievalAgentProgressKey(sessionId, usecaseId, datasetName) {
    if (sessionId && datasetName) return `retrieval_agent_${sessionId}__${datasetName}`;
    if (sessionId) return `retrieval_agent_${sessionId}`;
    if (usecaseId && datasetName) return `retrieval_agent_${usecaseId}__${datasetName}`;
    return usecaseId ? `retrieval_agent_${usecaseId}` : '';
}

export function isRetrievalAgentProgressKey(key) {
    return Boolean(key && String(key).startsWith('retrieval_agent_'));
}

export function retrievalAgentBarItem({
    usecaseId,
    sessionId,
    datasetName,
    status = 'queued',
    message,
    isError = false,
    errorDetails,
    jobsAhead,
} = {}) {
    if (!usecaseId && !sessionId) return null;
    const terminalError = Boolean(isError) || (RETRIEVAL_AGENT_TERMINAL_STATUSES.has(status) && status !== 'completed');
    const isCompleted = status === 'completed';
    const searchingMessage =
        normalizePhase(status) === 'data_searching' ? message || SEARCHING_DATASETS_MESSAGE : message || undefined;
    return {
        usecaseId,
        sessionId,
        datasetName: datasetName || 'Retrieval Agent',
        isRetrievalAgent: true,
        status,
        phase: status,
        message: searchingMessage,
        details: terminalError ? errorDetails || undefined : undefined,
        isError: terminalError,
        isCompleted,
        // Only carry the key when a position is known, so merging a WebSocket
        // update (which has no position) keeps the last polled value.
        ...(typeof jobsAhead === 'number' && Number.isFinite(jobsAhead) ? { jobsAhead } : {}),
    };
}

export function seedRetrievalAgentProgressItem({
    usecaseId,
    sessionId,
    datasetName,
    status = 'queued',
    jobsAhead,
} = {}) {
    return retrievalAgentBarItem({ usecaseId, sessionId, datasetName, status, jobsAhead });
}

export function shouldShowRetrievalUploadGroup(file) {
    if (!file) return false;
    return Boolean(
        file.hasDatasetUpload ||
            file.status === 'uploading_dataset' ||
            file.phase === 'uploading' ||
            file.phase === 'processing'
    );
}

export function deriveRetrievalAgentSteps(file) {
    const status = file?.status || file?.phase || 'queued';
    const isError = Boolean(file?.isError);
    const isCompleted = Boolean(file?.isCompleted) || status === 'completed';
    const showUploadGroup = shouldShowRetrievalUploadGroup(file);
    const stepIds = showUploadGroup ? RETRIEVAL_STEPS_BEFORE_UPLOAD : VISIBLE_STEPS;
    const phase = normalizePhase(status);
    const activeIndex = stepIds.indexOf(phase);

    return stepIds.map((id, index) => {
        let state = 'pending';
        if (showUploadGroup && !(isError && !file?.hasDatasetUpload)) {
            state = 'complete';
        } else if (isCompleted) {
            state = 'complete';
        } else if (isError && (index === activeIndex || (activeIndex < 0 && index === 0))) {
            state = 'error';
        } else if (isError && activeIndex >= 0 && index < activeIndex) {
            state = 'complete';
        } else if (activeIndex >= 0 && index < activeIndex) {
            state = 'complete';
        } else if (index === activeIndex) {
            state = 'active';
        }
        let subtitle = null;
        if (state === 'active' && id === 'data_searching') {
            subtitle = SEARCHING_DATASETS_MESSAGE;
        } else if (state === 'active' && id === 'queued') {
            subtitle = queuedWaitingSubtitle(file?.jobsAhead);
        }
        return {
            id,
            label: STEP_LABELS[id],
            state,
            subtitle,
        };
    });
}

/**
 * Active Retrieval Agent rows from `progressStatus` for one task (non-terminal).
 * Counterpart of `listActiveAiGenerationEntriesForUsecase` for agent sessions.
 */
export function listActiveRetrievalAgentEntriesForUsecase(progressStatus, usecaseId) {
    if (!progressStatus || !usecaseId) return [];
    return Object.entries(progressStatus)
        .filter(([, value]) => {
            if (!value?.isRetrievalAgent) return false;
            if (value.isCompleted || value.isError) return false;
            if (RETRIEVAL_AGENT_TERMINAL_STATUSES.has(value.status)) return false;
            return String(value.usecaseId) === String(usecaseId);
        })
        .map(([progressKey, value]) => ({ ...value, progressKey }));
}

/**
 * True while a Retrieval Agent run for *datasetName* is still in flight
 * (non-terminal entry in `progressStatus`). Drives the "In progress" loading
 * state of the details modal's "Datasets used in retrieval" row.
 */
export function isRetrievalAgentInProgressForDataset(progressStatus, datasetName) {
    if (!progressStatus || !datasetName) return false;
    return Object.values(progressStatus).some((value) => {
        if (!value?.isRetrievalAgent) return false;
        if (value.isCompleted || value.isError) return false;
        if (RETRIEVAL_AGENT_TERMINAL_STATUSES.has(value.status)) return false;
        return String(value.datasetName) === String(datasetName);
    });
}

export function sessionsFromRetrievalAgentStatus(result) {
    if (!result) return [];
    if (Array.isArray(result.sessions)) {
        return result.sessions.filter((session) => session && !RETRIEVAL_AGENT_TERMINAL_STATUSES.has(session.status));
    }
    return [];
}

export function retrievalAgentItemsFromStatus(result, usecaseId) {
    return sessionsFromRetrievalAgentStatus(result)
        .map((session) =>
            retrievalAgentBarItem({
                usecaseId,
                sessionId: session.session_id,
                datasetName: session.dataset_name,
                status: session.status,
                message: session.message,
                isError: session.is_error,
                errorDetails: session.error_details,
                jobsAhead: session.jobs_ahead,
            })
        )
        .filter(Boolean);
}

function _legacyRetrievalAgentKeys(sessionId, usecaseId) {
    const keys = [];
    if (sessionId) keys.push(`retrieval_agent_${sessionId}`);
    if (usecaseId) keys.push(`retrieval_agent_${usecaseId}`);
    return keys;
}

export function mergeRetrievalAgentProgressIntoStatus(prev, item) {
    if (!item?.isRetrievalAgent) return prev;
    const key = retrievalAgentProgressKey(item.sessionId, item.usecaseId, item.datasetName);
    if (!key) return prev;
    let next = prev;
    let existing = prev?.[key];
    for (const legacyKey of _legacyRetrievalAgentKeys(item.sessionId, item.usecaseId)) {
        if (legacyKey === key || !prev?.[legacyKey]?.isRetrievalAgent) continue;
        const legacyName = prev[legacyKey].datasetName;
        if (legacyName && item.datasetName && legacyName !== item.datasetName) continue;
        existing = { ...prev[legacyKey], ...(existing || {}) };
        if (next === prev) next = { ...prev };
        delete next[legacyKey];
    }
    if (item.isCompleted && !existing) return next;
    if (item.status === 'queued' && existing?.status && existing.status !== 'queued') {
        return next === prev ? prev : next;
    }
    if (item.isCompleted && existing && !existing.isError) {
        return {
            ...next,
            [key]: {
                ...existing,
                message: item.message ?? existing.message,
                agentFinished: true,
                ...retrievalUploadHandoffFields(existing),
            },
        };
    }
    const handedOff = item.status === 'uploading_dataset';
    return {
        ...next,
        [key]: {
            ...(existing || {}),
            ...item,
            ...(handedOff ? retrievalUploadHandoffFields(existing) : {}),
        },
    };
}

function retrievalUploadHandoffFields(existing) {
    return {
        hasDatasetUpload: true,
        status: 'uploading_dataset',
        phase: existing?.hasDatasetUpload ? existing.phase : 'uploading',
        uploadProgress: existing?.uploadProgress ?? 0,
    };
}

function retrievalAgentKeysForDataset(prev, datasetName) {
    if (!prev || !datasetName) return [];
    return Object.entries(prev)
        .filter(([, value]) => value?.isRetrievalAgent && value.datasetName === datasetName)
        .map(([key]) => key);
}

/**
 * Progress key of the Retrieval Agent card for *datasetName*, or null.
 * Used to route an end-of-ingest TRAJECTORY ``updateGenerationProgress``
 * (agentic tasks) onto the card instead of opening a standalone one.
 */
export function findRetrievalAgentProgressKeyForDataset(prev, datasetName) {
    const keys = retrievalAgentKeysForDataset(prev, datasetName);
    return keys.length ? keys[0] : null;
}

/**
 * Flag the Retrieval Agent card as the owner of the chained trajectory
 * generation so `foldGenerationProgressIntoUploadItem` and the
 * "Generating trajectories" step row apply to it.
 */
export function markRetrievalAgentTrajectoryGeneration(item) {
    if (!item) return item;
    if (item.isLabelGeneration && item.isTrajectoryGeneration && item.hasDatasetUpload) return item;
    return {
        ...item,
        isLabelGeneration: true,
        isTrajectoryGeneration: true,
        hasDatasetUpload: true,
        status: 'uploading_dataset',
        uploadProgress: Math.max(item.uploadProgress || 0, 100),
        processingProgress: Math.max(item.processingProgress || 0, 100),
    };
}

/**
 * Merge generic dataset upload/processing onto the Retrieval Agent card so
 * both step groups stay visible. Drops the standalone upload card.
 */
export function foldDatasetProgressIntoRetrievalAgent(
    prev,
    { datasetName, progressKey, isCompleted = false, isError = false } = {}
) {
    const keys = retrievalAgentKeysForDataset(prev, datasetName);
    if (!keys.length) return prev;
    const next = { ...prev };
    const uploadEntry =
        progressKey && next[progressKey] && !next[progressKey].isRetrievalAgent ? next[progressKey] : null;
    if (progressKey && uploadEntry) {
        delete next[progressKey];
    }
    keys.forEach((key) => {
        const existing = next[key];
        next[key] = {
            ...existing,
            ...(uploadEntry || {}),
            usecaseId: existing.usecaseId,
            sessionId: existing.sessionId,
            datasetName: existing.datasetName,
            isRetrievalAgent: true,
            hasDatasetUpload: true,
            status: 'uploading_dataset',
            message: existing.message,
            details: isError ? uploadEntry?.details ?? existing.details : existing.details,
            isCompleted: Boolean(isCompleted) && !isError,
            isError: Boolean(isError) || Boolean(existing.isError),
        };
    });
    return next;
}
